import { useEffect, useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import AuthLayout from '@/views/auth-layout'
import { signupStatus, logout, SignupStatus } from '@/api/auth'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { Clock3, XCircle } from 'lucide-react'

const POLL_INTERVAL_MS = 15000

export default function PendingApprovalPage() {
  const { t } = useTranslation()
  const nav = useNavigate()
  const [status, setStatus] = useState<SignupStatus | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    const poll = async () => {
      try {
        const s = await signupStatus()
        if (cancelled) return
        setStatus(s)
        if (s.approval_status === 'approved') {
          nav({ to: '/dashboard' })
        }
      } catch {
        // keep showing last known status; next poll will retry
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    poll()
    const interval = setInterval(poll, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [nav])

  const isRejected = status?.approval_status === 'rejected'

  const dayLabel = (d: string) => t(`day_${d}`, { defaultValue: d })
  const slotLabel = (s?: string | null) => s ? t(`slot_${s}`, { defaultValue: s }) : '-'

  return (
    <AuthLayout>
      <Card className='p-6 max-w-md'>
        <div className='flex flex-col items-center space-y-3 text-center'>
          {isRejected ? (
            <XCircle className='w-10 h-10 text-destructive' />
          ) : (
            <Clock3 className='w-10 h-10 text-primary' />
          )}
          <h1 className='text-2xl font-semibold tracking-tight'>
            {isRejected
              ? t('signup_rejected_title', { defaultValue: 'Request rejected' })
              : t('signup_pending_title', { defaultValue: 'Your account is pending approval' })}
          </h1>
          <p className='text-sm text-muted-foreground'>
            {isRejected
              ? (status?.rejection_reason || t('signup_rejected_desc', { defaultValue: 'Please contact support for more information.' }))
              : t('signup_pending_desc', { defaultValue: 'Our team reviews every new cabinet. We will contact you during your selected availability.' })}
          </p>

          {!isRejected && status && (
            <div className='w-full text-sm bg-muted rounded-lg p-4 space-y-1 text-left'>
              <div>
                <span className='text-muted-foreground'>{t('cabinet_name', { defaultValue: 'Cabinet name' })}:</span>{' '}
                {status.cabinet_name || '-'}
              </div>
              <div>
                <span className='text-muted-foreground'>{t('availability_days', { defaultValue: 'Available days' })}:</span>{' '}
                {status.availability_days?.length ? status.availability_days.map(dayLabel).join(', ') : '-'}
              </div>
              <div>
                <span className='text-muted-foreground'>{t('availability_slot', { defaultValue: 'Preferred time' })}:</span>{' '}
                {slotLabel(status.availability_slot)}
              </div>
            </div>
          )}

          {!loading && !status?.approval_status && (
            <p className='text-sm text-muted-foreground'>{t('serv_error', { defaultValue: 'Something went wrong. Please try again.' })}</p>
          )}
        </div>

        <div className='pt-6 flex justify-center'>
          <Button variant='outline' onClick={() => logout().finally(() => nav({ to: '/auth/login' }))}>
            {t('logout', { defaultValue: 'Log out' })}
          </Button>
        </div>
      </Card>
    </AuthLayout>
  )
}
